// PeoplePicker — generic async combobox over a server-searched, paged list of
// people (Universal Work design §5/§10). The caller binds `fetchPage` to an
// endpoint that already applies the caller's scope; this component never
// filters people client-side and never downloads a roster. Permission-aware
// empty states come from the page's `reason` (200 + reason, never a raw 403).
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Search, X } from 'lucide-react';
import type { EligiblePage, EligiblePerson, EligibleReason } from '@/services/workService';

export type Person = EligiblePerson;
export type { EligiblePage };

interface CommonProps {
  id: string;
  fetchPage: (q: string, page: number) => Promise<EligiblePage>;
  placeholder?: string;
  disabled?: boolean;
  /** Single mode: show the remove (×) on the selected chip. Default true. */
  allowClear?: boolean;
  /** Shown under "No eligible employees found" (e.g. "Ask your manager to assign this."). */
  emptyHint?: string;
  /** Employee ids never offered (current owner on reassign, existing contributors…). */
  exclude?: string[];
  /** Purpose-specific copy for the POLICY_DISABLED empty state (e.g. "Your
   * company hasn't enabled collaborators"); defaults to the generic line. */
  policyDisabledMessage?: string;
  /** Debounce for server search; tests pass 0. */
  debounceMs?: number;
  className?: string;
  /** Compact input (right-rail panels). */
  size?: 'sm' | 'md';
  'aria-label'?: string;
}
interface SingleProps extends CommonProps {
  multiple?: false;
  value: Person | null;
  onChange: (p: Person | null) => void;
}
interface MultiProps extends CommonProps {
  multiple: true;
  value: Person[];
  onChange: (p: Person[]) => void;
}
export type PeoplePickerProps = SingleProps | MultiProps;

export const initialsOf = (name: string): string =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase() || '').join('') || '?';

// Static class map — Tailwind only emits classes it can see at build time.
const AVATAR_SIZE: Record<5 | 6 | 7, string> = { 5: 'h-5 w-5 text-[9px]', 6: 'h-6 w-6 text-[10px]', 7: 'h-7 w-7 text-[11px]' };
const PersonAvatar: React.FC<{ p: Person; size?: 5 | 6 | 7 }> = ({ p, size = 7 }) => (
  p.avatar_url
    ? <img src={p.avatar_url} alt="" className={`${AVATAR_SIZE[size]} rounded-full object-cover shrink-0`} />
    : <span className={`${AVATAR_SIZE[size]} rounded-full bg-[#FEF0E4] text-[#D9711E] font-bold inline-flex items-center justify-center shrink-0`}>
        {initialsOf(p.display_name)}
      </span>
);

export const personSubline = (p: Person): string =>
  [p.job_title, p.department_name].filter(Boolean).join(' · ');

/** Workload hint on a picker row — describes the WORK ("High workload · 3
 * overdue"), never the person. Present only when the caller asked for it. */
export const workloadHint = (w: NonNullable<Person['workload']>): string => {
  const band: Record<string, string> = { LOW: 'Low workload', NORMAL: 'Normal workload', HIGH: 'High workload', VERY_HIGH: 'Very high workload' };
  const parts = [band[w.band] || String(w.band).toLowerCase(), `${w.active} active`];
  if (w.overdue > 0) parts.push(`${w.overdue} overdue`);
  if (w.due_soon > 0) parts.push(`${w.due_soon} due soon`);
  return parts.join(' · ');
};

export const PeoplePicker: React.FC<PeoplePickerProps> = (props) => {
  const {
    id, fetchPage, placeholder, disabled, allowClear = true, emptyHint, exclude, debounceMs = 250,
    className = '', size = 'md', policyDisabledMessage,
  } = props;
  const { t } = useTranslation('work');
  const listId = `${id}-listbox`;
  const helperId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [items, setItems] = useState<Person[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [reason, setReason] = useState<EligibleReason>('OK');
  const [scope, setScope] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [active, setActive] = useState(-1);
  // Guards against out-of-order responses (fast typing, page-more racing a new search).
  const reqSeq = useRef(0);

  const selectedIds = useMemo(() => {
    const s = new Set<string>(exclude || []);
    if (props.multiple) props.value.forEach((p) => s.add(p.employee_id));
    else if (props.value) s.add(props.value.employee_id);
    return s;
  }, [exclude, props.multiple, props.value]);
  const visible = useMemo(() => items.filter((p) => !selectedIds.has(p.employee_id)), [items, selectedIds]);

  const runSearch = useCallback((query: string) => {
    const seq = ++reqSeq.current;
    setLoading(true); setError(false);
    fetchPage(query, 1)
      .then((res) => {
        if (seq !== reqSeq.current) return;
        setItems(res.items); setPage(1); setHasMore(res.has_more);
        setReason(res.reason); setScope(res.scope_summary); setActive(res.items.length ? 0 : -1);
      })
      .catch(() => { if (seq === reqSeq.current) { setItems([]); setHasMore(false); setError(true); } })
      .finally(() => { if (seq === reqSeq.current) setLoading(false); });
  }, [fetchPage]);

  // Debounced server search while open.
  useEffect(() => {
    if (!open) return;
    const h = setTimeout(() => runSearch(q.trim()), debounceMs);
    return () => clearTimeout(h);
  }, [q, open, runSearch, debounceMs]);

  const loadMore = useCallback(() => {
    if (!hasMore || loadingMore || loading) return;
    const seq = reqSeq.current;
    const next = page + 1;
    setLoadingMore(true);
    fetchPage(q.trim(), next)
      .then((res) => {
        if (seq !== reqSeq.current) return;
        setItems((prev) => {
          const seen = new Set(prev.map((p) => p.employee_id));
          return [...prev, ...res.items.filter((p) => !seen.has(p.employee_id))];
        });
        setPage(next); setHasMore(res.has_more);
      })
      .catch(() => { if (seq === reqSeq.current) setHasMore(false); })
      .finally(() => { if (seq === reqSeq.current) setLoadingMore(false); });
  }, [hasMore, loadingMore, loading, page, q, fetchPage]);

  const close = () => { setOpen(false); setActive(-1); };

  const pick = (p: Person) => {
    if (props.multiple) {
      props.onChange([...props.value, p]);
      setQ('');
      inputRef.current?.focus();
    } else {
      props.onChange(p);
      setQ('');
      close();
    }
  };
  const remove = (employeeId: string) => {
    if (props.multiple) props.onChange(props.value.filter((p) => p.employee_id !== employeeId));
    else props.onChange(null);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      setActive((a) => Math.min(visible.length - 1, a + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === 'Enter') {
      if (open && active >= 0 && visible[active]) { e.preventDefault(); pick(visible[active]); }
    } else if (e.key === 'Escape') {
      if (open) { e.preventDefault(); close(); }
    } else if (e.key === 'Backspace' && !q && props.multiple && props.value.length) {
      remove(props.value[props.value.length - 1].employee_id);
    }
  };

  // Keep the active option in view.
  useEffect(() => {
    if (active < 0 || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-idx="${active}"]`);
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [active]);

  // Close on outside click.
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (ev: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(ev.target as Node)) close();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const onListScroll = (e: React.UIEvent<HTMLUListElement>) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) loadMore();
  };

  const inputCls = `w-full rounded-md border border-border bg-background text-foreground ${size === 'sm' ? 'text-xs px-2 py-1.5 pl-7' : 'text-sm px-2.5 py-2 pl-8'} disabled:opacity-60`;
  const single = !props.multiple ? props.value : null;

  const permissionMessage = reason === 'NO_CAPABILITY'
    ? t('picker_no_capability', { defaultValue: 'You do not have permission to assign work' })
    : reason === 'POLICY_DISABLED'
      ? (policyDisabledMessage || t('picker_policy_disabled', { defaultValue: "Your company hasn't enabled this" }))
      : null;

  // An untouched picker (no query, nothing to list, no error) keeps its panel
  // closed — a bare "No eligible employees found" on focus reads as a failure
  // and, inside dialogs, covers the content underneath.
  const idleEmpty = !loading && !permissionMessage && !error && visible.length === 0 && !q.trim();
  return (
    <div ref={rootRef} className={`relative ${className}`}>
      {props.multiple && props.value.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {props.value.map((p) => (
            <span key={p.employee_id} data-testid="people-chip"
              className="inline-flex items-center gap-1 pl-1 pr-1 py-0.5 rounded-full bg-muted text-foreground text-xs">
              <PersonAvatar p={p} size={5} />
              <span className="max-w-[10rem] truncate">{p.display_name}</span>
              {!disabled && (
                <button type="button" className="rounded-full hover:bg-background p-0.5 text-muted-foreground hover:text-foreground"
                  aria-label={t('picker_remove', { defaultValue: 'Remove {{name}}', name: p.display_name }) as string}
                  onClick={() => remove(p.employee_id)}>
                  <X className="w-3 h-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {single ? (
        <div data-testid="people-chip"
          className={`flex items-center gap-2 rounded-md border border-border bg-background ${size === 'sm' ? 'px-2 py-1 text-xs' : 'px-2.5 py-1.5 text-sm'}`}>
          <PersonAvatar p={single} size={size === 'sm' ? 5 : 6} />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-foreground font-medium">{single.display_name}</span>
            {personSubline(single) && <span className="block truncate text-[11px] text-muted-foreground">{personSubline(single)}</span>}
          </span>
          {allowClear && !disabled && (
            <button type="button" className="text-muted-foreground hover:text-foreground shrink-0"
              aria-label={t('picker_remove', { defaultValue: 'Remove {{name}}', name: single.display_name }) as string}
              onClick={() => { remove(single.employee_id); setTimeout(() => inputRef.current?.focus(), 0); }}>
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      ) : (
        <div className="relative">
          <Search className={`absolute ${size === 'sm' ? 'left-2 w-3.5 h-3.5' : 'left-2.5 w-4 h-4'} top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none`} />
          <input
            ref={inputRef}
            id={id}
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={open && active >= 0 && visible[active] ? `${listId}-opt-${visible[active].employee_id}` : undefined}
            aria-describedby={helperId}
            aria-label={props['aria-label']}
            autoComplete="off"
            className={inputCls}
            placeholder={placeholder || (t('picker_placeholder', { defaultValue: 'Search people…' }) as string)}
            disabled={disabled}
            value={q}
            onChange={(e) => { setQ(e.target.value); if (!open) setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
          />
        </div>
      )}

      <p id={helperId} className="mt-1 text-[11px] text-muted-foreground">
        {reason === 'OK' && scope
          ? t('picker_helper_scope', { defaultValue: 'Search by name, job title, or department · scope: {{scope}}', scope })
          : t('picker_helper', { defaultValue: 'Search by name, job title, or department' })}
      </p>

      {open && !single && !idleEmpty && (
        <div className="absolute z-30 mt-1 w-full rounded-md border border-border bg-background shadow-md">
          {loading && (
            <div className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground" role="status">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> {t('picker_loading', { defaultValue: 'Searching…' })}
            </div>
          )}
          {!loading && permissionMessage && (
            <div className="px-3 py-3 text-xs text-muted-foreground" role="status" data-testid="people-permission">
              <p className="text-foreground">{permissionMessage}</p>
              {scope && <p className="mt-0.5">{scope}</p>}
            </div>
          )}
          {!loading && !permissionMessage && error && (
            <div className="px-3 py-3 text-xs text-danger-strong" role="alert">
              {t('picker_error', { defaultValue: 'Could not search people. Try again.' })}
            </div>
          )}
          {!loading && !permissionMessage && !error && visible.length === 0 && (
            <div className="px-3 py-3 text-xs text-muted-foreground" role="status" data-testid="people-empty">
              <p className="text-foreground">{t('picker_empty', { defaultValue: 'No eligible employees found' })}</p>
              {emptyHint && <p className="mt-0.5">{emptyHint}</p>}
            </div>
          )}
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            aria-label={placeholder || (t('picker_placeholder', { defaultValue: 'Search people…' }) as string)}
            className="max-h-64 overflow-y-auto py-1"
            onScroll={onListScroll}
            hidden={loading || !!permissionMessage || visible.length === 0}
          >
            {visible.map((p, i) => (
              <li
                key={p.employee_id}
                id={`${listId}-opt-${p.employee_id}`}
                data-idx={i}
                role="option"
                aria-selected={i === active}
                className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer ${i === active ? 'bg-muted' : 'hover:bg-muted/60'}`}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(p)}
              >
                <PersonAvatar p={p} />
                <span className="min-w-0">
                  <span className="block truncate text-sm text-foreground">{p.display_name}</span>
                  {personSubline(p) && <span className="block truncate text-[11px] text-muted-foreground">{personSubline(p)}</span>}
                  {p.workload && (
                    <span className="block truncate text-[11px] text-muted-foreground" data-testid={`picker-workload-${p.employee_id}`}>
                      {workloadHint(p.workload)}
                    </span>
                  )}
                </span>
              </li>
            ))}
            {hasMore && (
              <li role="presentation" className="px-3 py-1.5">
                <button type="button" className="w-full text-center text-xs text-primary-500 hover:underline disabled:opacity-60"
                  disabled={loadingMore} onMouseDown={(e) => e.preventDefault()} onClick={loadMore}>
                  {loadingMore ? t('picker_loading_more', { defaultValue: 'Loading…' }) : t('picker_more', { defaultValue: 'Load more' })}
                </button>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default PeoplePicker;
